import { cn } from "../../../utils/cn.js";
import { cva } from "class-variance-authority";
import { XIcon } from "@phosphor-icons/react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Tag/Tag.tsx
const tagDotVariants = cva("size-xs rounded-full shrink-0", { variants: { color: {
	brickwork: "bg-icon-accent-brickwork",
	cement: "bg-icon-accent-cement",
	craneberry: "bg-icon-accent-craneberry",
	danger: "bg-icon-danger",
	hivis: "bg-icon-accent-hivis",
	information: "bg-icon-information",
	success: "bg-icon-success",
	warning: "bg-icon-warning"
} } });
const tagVariants = cva([
	"inline-flex items-center gap-xxs rounded-xs",
	"border-(length:--border-width-default) border-solid border-border-primary",
	"font-family-accent font-accent-semibold uppercase tracking-[0.8px] whitespace-nowrap"
], {
	variants: { size: {
		sm: "px-xs py-button-height text-accent-medium leading-accent-small text-text-secondary",
		base: "h-[25px] px-sm py-button-height text-body-small leading-body-small text-text-primary"
	} },
	defaultVariants: { size: "sm" }
});
const tagDismissButtonVariants = cva("inline-flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-icon-secondary transition-colors hover:text-icon-primary", {
	variants: { size: {
		sm: "",
		base: ""
	} },
	defaultVariants: { size: "sm" }
});
const NAMED_COLORS = /* @__PURE__ */ new Set([
	"brickwork",
	"cement",
	"craneberry",
	"danger",
	"hivis",
	"information",
	"success",
	"warning"
]);
function isNamedColor(value) {
	return NAMED_COLORS.has(value);
}
/**
* Curri-styled tag for categorising and labelling items.
*
* Renders an uppercase monospace label inside a bordered pill. An optional
* colored indicator dot is shown when the `color` prop is provided. Accepts
* named design-token colors for consistency or arbitrary CSS colors for
* one-off use cases.
*
* @param props - Tag configuration
*
* When `onDismiss` is provided, an inline dismiss button is rendered inside the
* tag. Use `size="base"` when the tag should align with standard base-height
* form controls or buttons.
*
* @returns The rendered tag element
*/
function Tag({ className, color, label, onDismiss, size, ...props }) {
	const resolvedColor = color ?? "var(--color-icon-brand)";
	const named = isNamedColor(resolvedColor);
	return /* @__PURE__ */ jsxs("span", {
		"data-slot": "tag",
		className: cn(tagVariants({ size }), className),
		...props,
		children: [
			/* @__PURE__ */ jsx("span", {
				className: cn("size-xs rounded-full shrink-0 border-(length:--border-width-default) border-solid border-border-primary", named && tagDotVariants({ color: resolvedColor })),
				style: named ? void 0 : { backgroundColor: resolvedColor },
				"aria-hidden": "true"
			}),
			label,
			onDismiss && /* @__PURE__ */ jsx("button", {
				type: "button",
				"aria-label": `Remove ${label}`,
				className: tagDismissButtonVariants({ size }),
				onClick: (event) => {
					event.stopPropagation();
					onDismiss();
				},
				children: /* @__PURE__ */ jsx(XIcon, {
					size: 10,
					weight: "bold"
				})
			})
		]
	});
}
//#endregion
export { Tag, tagDotVariants };

//# sourceMappingURL=Tag.js.map